import apiClient from 'api/common/common'

/**
 * BPO審査リクエストの型定義
 */
export interface BpoRequestRequest {
  verificationId: string
  errorType: string
  failureReason?: string
}

/**
 * BPO審査リクエストのレスポンス型定義
 */
export interface BpoRequestResponse {
  status: 'success' | 'error'
  response?: {
    requestId: number
    status: string
    message: string
  }
  errors?: string[]
}

/**
 * BPO審査状況の実際のデータ型定義
 */
export interface BpoStatusData {
  requestId: number
  status: 'pending' | 'processing' | 'approved' | 'rejected' | 'error' | 'timeout'
  progress: number
  message: string
  createdAt: string
  estimatedCompletionTime: string
  /** BPO用verificationId（TRUSTDOCK Uploader APIで作成） */
  bpoVerificationId?: string
  /** 元のverificationId（Browser SDKで作成） */
  originalVerificationId?: string
}

/**
 * BPO審査状況のレスポンス型定義
 */
export interface BpoStatusResponse {
  status: 'success' | 'error'
  response?: BpoStatusData
  errors?: string[]
}

/**
 * BPO失敗回数のレスポンス型定義
 */
export interface BpoFailureCountResponse {
  status: 'success' | 'error'
  response?: {
    failureCount: number
    canRetry: boolean
    maxRetries: number
  }
  errors?: string[]
}

/**
 * BPO審査状況を取得する
 * @param requestId BPO審査リクエストID
 * @returns BPO審査状況
 */
export const getBpoStatusAPI = async (requestId: number): Promise<BpoStatusData> => {
  try {
    const response = await apiClient.get(`/bpo/status/${requestId}`)
    return response.data
  } catch (error: any) {
    console.error('BPO審査状況取得エラー:', error)
    // 404エラーは呼び出し元で処理するためそのままthrow
    if (error.response?.status === 404) {
      throw error
    }
    // その他のエラーもthrow
    throw error
  }
}

/**
 * BPO失敗回数を取得する
 * @returns BPO失敗回数情報
 */
export const getBpoFailureCountAPI = async (): Promise<BpoFailureCountResponse> => {
  try {
    const response = await apiClient.get('/bpo/failure-count')
    return response.data
  } catch (error: any) {
    console.error('BPO失敗回数取得エラー:', error)
    // ネットワークエラーの判定
    if (!error.response && error.request) {
      return {
        status: 'error',
        errors: ['ネットワークに接続できません。インターネット接続を確認してください。']
      }
    }
    return {
      status: 'error',
      errors: ['BPO失敗回数の取得に失敗しました']
    }
  }
}

/**
 * BPO審査リクエストを作成する
 * @param request BPO審査リクエスト情報
 * @returns BPO審査リクエスト作成結果
 */
export const createBpoRequestAPI = async (request: BpoRequestRequest): Promise<BpoRequestResponse> => {
  try {
    const response = await apiClient.post('/bpo/request', request)
    return response.data
  } catch (error: any) {
    console.error('BPO審査リクエスト作成エラー:', error)
    // ネットワークエラーの判定
    if (!error.response && error.request) {
      return {
        status: 'error',
        errors: ['ネットワークに接続できません。インターネット接続を確認してください。']
      }
    }
    return {
      status: 'error',
      errors: ['BPO審査リクエストの作成に失敗しました']
    }
  }
}